<script>

/**
 * @param manifest { Object }
 * @property manifest.id { Number } マニフェストID
 * @property manifest.enable_copy { Boolean } 複写ボタン表示対象
 * @property manifest.target_for_registration { Boolean } 登録対象
 * @property manifest.use_report_transport_quantity { Boolean } 運搬量/運搬量の単位の必須可否
 * @property manifest.report_transport_quantity { Number } 運搬量
 * @property manifest.report_transport_quantity_unit_id { Number } 運搬量の単位ID
 * @property manifest.report_valuables_collecting_quantity { Number } 有価物拾集量
 * @property manifest.report_valuables_collecting_quantity_unit_id { Number } 有価物拾集量の単位ID
 */
export default {
  name: "CompletionQuantityInformation",
  props: {
    /**
     * マニフェスト
     */
    manifest: {
      type: Object,
      default: () => {},
    },
    /**
     * 最初のフィールド
     */
    isFirstField: {
      type: Boolean,
      default: () => false,
    },
    /**
     * 運搬量の単位
     */
    quantityUnits: {
      type: Array,
      default: () => [],
    },
    /**
     * 有価物拾集量の単位
     */
    valuablesQuantityUnits: {
      type: Array,
      default: () => [],
    },
    callerType: {
      type: String,
      default: () => null,
    },
    /**
     *
     */
    processType: {
      type: String,
      default: () => null,
    },

  },
  data () {
    return {
      customMessages: {
        double: '{_field_}は半角数字のみ使用できます',
        regex: '{_field_}は整数5桁まで,小数3桁までで入力してください'
      },
      /**
       * 区間
       */
      fullSections: [
        { value: 1, text: "区間１" },
        { value: 2, text: "区間２" },
        { value: 3, text: "区間３" },
        { value: 4, text: "区間４" },
        { value: 5, text: "区間５" },
      ],
    }
  },
  mounted() {
    /**
     * 初期ロード
     * 指定マニフェストの指定区間の情報を再取得.
     */
    this.sectionChangeEvent();
  },
  computed: {
    valuableCollectingUnitToBe () {
      if (this.manifest.report_valuables_collecting_quantity) {
        return this.manifest.report_valuables_collecting_quantity > 0
      }
      return false
    },
    isReportTransportQuantityRequired() {
      // 一廃以外の場合
      if (this.manifest.order_type !== 'general') {
        return (
          this.manifest.use_report_transport_quantity || this.manifest.report_transport_quantity > 0 || this.manifest.report_transport_quantity_unit_id > 0
        )
      }
      // 一廃の場合
      return (
        this.manifest.report_transport_quantity > 0 || this.manifest.report_transport_quantity_unit_id > 0
      )
    }
  },
  watch: {},
  created() {
  },
  methods: {
    /**
     * 複写ボタン クリックイベント
     */
    copyFieldButtonClickEvent (property, value) {
      this.$emit("copyField", property, value);
    },
    /**
     * 区間 チェンジイベント
     * マニフェストID, 区間番号を呼び出し元へ伝達
     */
    sectionChangeEvent () {
      this.$emit("sectionChange", this.manifest.id, this.manifest.section)
    },
  }
}
</script>

<template>
  <div class="reportLayer">
    <v-row dense class="pb-3">
      <v-col cols="4">
        <h3>数量情報等</h3>

      </v-col>
      <v-col
        v-if="manifest.enable_copy"
        cols="8"
      >
        <v-btn block small style="visibility: hidden"></v-btn>
      </v-col>
    </v-row>

    <!--
    受注・発注で一括の場合は区間選択は不要
    -->
    <v-row
      v-if="!(['Requests', 'Accepts'].includes(processType) && callerType === 'bulk')"
      dense
    >
      <!-- 区間 -->
      <v-col cols="12">
        <template
          v-if="callerType === 'bulk'"
        >
          <validation-provider
            v-slot="{ errors }"
            :vid="`section-${manifest.id}`"
            rules="required"
            name="区間"
            class="requiredMark"
          >
            <v-select
              v-model="manifest.bulk_section"
              :error-messages="errors"
              :items="fullSections"
              dense
              hide-details="auto"
              label="区間"
              outlined
              persistent-placeholder
              @change="sectionChangeEvent"
            />
          </validation-provider>
        </template>
        <template
          v-else-if="!['self_emission', 'proxy_request'].includes(manifest.order_type) && callerType === 'completion_report'"
        >
          <validation-provider
            v-slot="{ errors }"
            :vid="`section-${manifest.id}`"
            rules="required"
            name="区間"
            class="requiredMark"
          >
            <v-select
              v-model="manifest.section"
              :disabled="!manifest.target_for_registration"
              :error-messages="errors"
              :items="manifest.sections"
              dense
              hide-details="auto"
              label="区間"
              outlined
              persistent-placeholder
              @change="sectionChangeEvent"
            >
              <template v-slot:append-outer>
                <div
                  v-if="callerType !== 'bulk'"
                  class="copy-actions"
                >
                </div>
              </template>
            </v-select>
          </validation-provider>

        </template>
      </v-col>
      <!-- /区間 -->
    </v-row>

    <v-row dense>
      <!-- 運搬量 -->
      <v-col cols="6">
        <validation-provider
          v-slot="{ errors }"
          :class="{'requiredMark': isReportTransportQuantityRequired }"
          :disabled="!manifest.target_for_registration"
          :rules="{ required: isReportTransportQuantityRequired, double: true, min_value: 0.001, max_value: 99999.999, quantityWithDecimal: { int_digits: 5, dec_digits: 3 } }"
          :vid="`report_transport_quantity-${manifest.id}`"
          name="運搬量"
          :custom-messages="customMessages"
        >
          <v-text-field
            v-model="manifest.report_transport_quantity"
            :disabled="!manifest.target_for_registration"
            :error-messages="errors"
            dense
            hide-details="auto"
            label="運搬量"
            outlined
            persistent-hint
            persistent-placeholder
            reverse
            type="number"
            @keydown.69.prevent
          >
            <template v-slot:append-outer>
              <div
                v-if="callerType !== 'bulk'"
                class="copy-actions"
              >
                <v-btn
                  v-if="manifest.enable_copy"
                  :disabled="!manifest.target_for_registration"
                  color="primary"
                  small
                  @click.stop="copyFieldButtonClickEvent('report_transport_quantity', manifest.report_transport_quantity)"
                >
                  <v-icon>mdi-chevron-double-down</v-icon>
                </v-btn>
              </div>
            </template>
          </v-text-field>
        </validation-provider>
      </v-col>
      <!-- /運搬量 -->

      <!-- 運搬量の単位 -->
      <v-col md="6">
        <validation-provider
          v-slot="{ errors }"
          :class="{ 'requiredMark': isReportTransportQuantityRequired }"
          :disabled="!manifest.target_for_registration"
          :rules="{ required: isReportTransportQuantityRequired }"
          :vid="`report_transport_quantity_unit_id-${manifest.id}`"
          name="運搬量の単位"
        >
          <v-select
            v-model="manifest.report_transport_quantity_unit_id"
            :disabled="!manifest.target_for_registration"
            :error-messages="errors"
            :items="quantityUnits"
            clearable
            dense
            hide-details="auto"
            label="運搬量の単位"
            outlined
            persistent-hint
            persistent-placeholder
            style="font-size: 100%"
          >
            <template v-slot:append-outer>
              <div
                v-if="callerType !== 'bulk'"
                class="copy-actions"
              >
                <v-btn
                  v-if="manifest.enable_copy"
                  :disabled="!manifest.target_for_registration"
                  color="primary"
                  small
                  @click.stop="copyFieldButtonClickEvent('report_transport_quantity_unit_id', manifest.report_transport_quantity_unit_id)"
                >
                  <v-icon>mdi-chevron-double-down</v-icon>
                </v-btn>
              </div>
            </template>
          </v-select>
        </validation-provider>
      </v-col>
    </v-row>
    <v-row dense>
      <!-- 有価物拾集量 -->
      <v-col cols="6">
        <validation-provider
          v-slot="{ errors }"
          :class="manifest.report_valuables_collecting_quantity_unit_id > 0 ? 'requiredMark' : ''"
          :disabled="!manifest.target_for_registration"
          :custom-messages="customMessages"
          :rules="{ required: manifest.report_valuables_collecting_quantity_unit_id > 0, double: true, min_value: 0.001, max_value: 99999.999, quantityWithDecimal: { int_digits: 5, dec_digits: 3 } }"
          :vid="`report_valuables_collecting_quantity-${manifest.id}`"
          name="有価物拾集量"
        >
          <v-text-field
            v-model="manifest.report_valuables_collecting_quantity"
            :disabled="!manifest.target_for_registration"
            :error-messages="errors"
            dense
            hide-details="auto"
            label="有価物拾集量"
            outlined
            persistent-hint
            persistent-placeholder
            reverse
            type="number"
            @keydown.69.prevent
          >
            <template v-slot:append-outer>
              <div
                v-if="callerType !== 'bulk'"
                class="copy-actions"
              >
                <v-btn
                  v-if="manifest.enable_copy"
                  :disabled="!manifest.target_for_registration"
                  color="primary"
                  small
                  @click.stop="copyFieldButtonClickEvent('report_valuables_collecting_quantity', manifest.report_valuables_collecting_quantity)"
                >
                  <v-icon>mdi-chevron-double-down</v-icon>
                </v-btn>
              </div>
            </template>
          </v-text-field>
        </validation-provider>
      </v-col>
      <!-- /有価物拾集量 -->

      <!-- 有価物拾集量の単位 -->
      <v-col md="6">
        <validation-provider
          v-slot="{ errors }"
          :class="valuableCollectingUnitToBe ? 'requiredMark' : ''"
          :disabled="!manifest.target_for_registration"
          :rules="{ required: valuableCollectingUnitToBe }"
          :vid="`report_valuables_collecting_quantity_unit_id-${manifest.id}`"
          name="有価物拾集量の単位"
        >
          <v-select
            v-model="manifest.report_valuables_collecting_quantity_unit_id"
            :disabled="!manifest.target_for_registration"
            :error-messages="errors"
            :items="valuablesQuantityUnits"
            clearable
            dense
            hide-details="auto"
            label="有価物拾集量の単位"
            outlined
            persistent-hint
            persistent-placeholder
            style="font-size: 100%"
          >
            <template v-slot:append-outer>
              <div
                v-if="callerType !== 'bulk'"
                class="copy-actions"
              >
                <v-btn
                  v-if="manifest.enable_copy"
                  :disabled="!manifest.target_for_registration"
                  color="primary"
                  small
                  @click.stop="copyFieldButtonClickEvent('report_valuables_collecting_quantity_unit_id', manifest.report_valuables_collecting_quantity_unit_id)"
                >
                  <v-icon>mdi-chevron-double-down</v-icon>
                </v-btn>
              </div>
            </template>
          </v-select>
        </validation-provider>
      </v-col>
      <!-- /有価物拾集量の単位 -->
    </v-row>
  </div>
</template>
